import type {
  AnnualRevenueRange as AnnualRevenueRangeContract,
  BusinessStage as BusinessStageContract,
  CompanyPartnerEnrollmentStatus,
  OptimusErrors,
  PartnerEnrollType as PartnerEnrollTypeContract,
  PartnerSource as PartnerSourceContract,
  WorkforceStructure as WorkforceStructureContract,
} from '@optimus/models';
import {
  ImproveIt360SolutionOrgName,
  InvoiceSimpleSolutionOrgName,
  KickservSolutionOrgName,
  MarketSharpSolutionOrgName,
  RoofsnapSolutionOrgName,
} from './constants';

type StringEnumContract<T extends Record<string, string>> = {
  [Key in keyof T]: `${T[Key]}`;
};

const browserEnum = <T extends Record<string, string>>(
  values: StringEnumContract<T>,
): T => values as unknown as T;

// eslint-disable-next-line no-shadow
export enum HDSetupFlowSteps {
  None = 'none',
  Step1 = 'step1',
  Step2 = 'step2',
  Step3 = 'step3',
  AllInstructionsSeen = 'all instructions seen',
  EmailCaptured = 'email captured',
  Done = 'done'
}

// eslint-disable-next-line no-shadow
export enum HDVerificationStatus {
  Pending = 'pending',
  Completed = 'completed',
  Error = 'error',
}

// eslint-disable-next-line no-shadow
export enum HDSupplyEnrollmentStatus {
  Pending = 'pending',
  Submitted = 'submitted',
  Done = 'done',
}

// Products offered in the pre-login support form picker. The value is the solution org name
// the backend uses to route the support email, the label is what the member sees. (SN-973)
export const SolutionOrgList = [
  { label: 'Invoice Simple', value: InvoiceSimpleSolutionOrgName },
  { label: 'Roofsnap', value: RoofsnapSolutionOrgName },
  { label: 'Kickserv', value: KickservSolutionOrgName },
  { label: 'MarketSharp', value: MarketSharpSolutionOrgName },
  { label: 'Improve It 360', value: ImproveIt360SolutionOrgName },
];

// eslint-disable-next-line no-shadow
export enum ShoppingStatus {
  Shopping = 'yes', // shops with partner
  NotShopping = 'no', // does not shop with partner
}

// These type-checked runtime values let CRA consume the API contract without bundling
// the uncompiled TypeScript sources from @optimus/models.
export const PartnerEnrollmentStatus = {
  NotStarted: 'not_started',
  ActionRequired: 'action_required',
  InProgress: 'in_progress',
  Completed: 'completed',
} as const satisfies StringEnumContract<typeof CompanyPartnerEnrollmentStatus>;

// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
export type PartnerEnrollmentStatus =
  typeof PartnerEnrollmentStatus[keyof typeof PartnerEnrollmentStatus];

// Error codes the app has to compare against at runtime. Declared here for the same reason
// as the enums above: importing the OptimusErrors namespace itself is a value import, which
// drags the uncompiled @optimus/models sources into the CRA bundle and fails the build.
// Only the codes the frontend actually branches on need an entry.
export const ApiErrorCode = {
  SsoMemberExistInOtherSolution: 'sso_member_exist_in_other_solution',
} as const satisfies Partial<StringEnumContract<typeof OptimusErrors>>;

export const PartnerSource = {
  Edge: 'edge',
  ServiceNation: 'service_nation',
} as const satisfies StringEnumContract<typeof PartnerSourceContract>;

// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
export type PartnerSource = typeof PartnerSource[keyof typeof PartnerSource];

export const PartnerEnrollType = {
  QuestionForm: 'question_form',
  ExistingAccountNumber: 'existing_account_number',
  Manufacturer: 'manufacturer',
  DirectEnroll: 'direct_enroll',
} as const satisfies StringEnumContract<typeof PartnerEnrollTypeContract>;

export const AnnualRevenueRange = browserEnum<typeof AnnualRevenueRangeContract>({
  Under100K: 'under_100k',
  Between100KAnd250K: '100k_250k',
  Between250KAnd500K: '250k_500k',
  Between500KAnd1M: '500k_1m',
  Between1MAnd5M: '1m_5m',
  Over5M: 'over_5m',
});

export const WorkforceStructure = browserEnum<typeof WorkforceStructureContract>({
  SoleOperator: 'sole_operator',
  FamilyBusiness: 'family_business',
  SmallCrew: 'small_crew',
  EstablishedTeam: 'established_team',
});

export const BusinessStage = browserEnum<typeof BusinessStageContract>({
  Foundation: 'foundation',
  Growth: 'growth',
  Scaling: 'scaling',
  Mature: 'mature',
});

// eslint-disable-next-line no-shadow
export enum BadgeType {
  Enrolled = 'enrolled',
  NotEnrolled = 'notEnrolled',
  Checkmark = 'checkmark',
  ManageRewards = 'manageRewards'
}

// eslint-disable-next-line no-shadow
export enum MemberPreferredLanguage {
  English = 'English',
  Español = 'Español',
}

// eslint-disable-next-line no-shadow
export enum CompanyBusinessType {
  SOLE_PROPRIETORSHIP = 'Sole Proprietorship',
  PARTNERSHIP = 'Partnership',
  CORPORATION = 'Corporation',
  LLC = 'LLC'
}

// eslint-disable-next-line no-shadow
export enum WalletListType {
  TRANSACTIONS = 'transactions',
  PARTNERSRELATION = 'partnersRelation',
  PAYOUT = 'payout'
}

// eslint-disable-next-line no-shadow
export enum PaymentMethodStatus {
  UNVERIFIED = 'unverified',
  PENDING = 'pending',
  VERIFIED = 'verified',
  INVALID = 'invalid',
  UNLINKED = 'unlinked',
}

// eslint-disable-next-line no-shadow
export enum OptimizelyFlagVariant {
  Default = 'default',
  First = 'first',
  Second = 'second',
}

// eslint-disable-next-line no-shadow
export enum PayoutDialogContentType {
  PAYMENT_METHOD_LIST = 'paymentMethodList',
  PAYPAL_CONNECT = 'paypalConnect',
  BANK_TRANSFER_FORM = 'bankTransferForm',
  PAPER_CHECK_DETAILS = 'paperCheckDetails',
  SET_ACTIVE_ACCOUNT = 'setActiveAccount',
  REMOVE_ACCOUNT = 'removeAccount'
}

// eslint-disable-next-line no-shadow
export enum PaymentMethodBankAccountType {
  CHECKING = 'CHECKING',
  SAVINGS = 'SAVINGS',
}

// eslint-disable-next-line no-shadow
export enum PaymentDetailStatusType {
  PAYMENT_FAILED = 'paymentFailed',
  PAYMENT_SUCCEEDED = 'paymentSucceeded',
  PAYMENT_UNCLAIMED = 'paymentUnclaimed',
  PAYMENT_RETURNED = 'paymentReturned',
}

// eslint-disable-next-line no-shadow
export enum ContentCategory {
  TOP_TRADE = 'top-trade',
  RECOMMENDED = 'recommended',
  FILTERED = 'filtered',
  REWARDS = 'rewards-and-more',
  VIDEO = 'video',
  PODCAST = 'podcast',
  TOPIC1 = 'marketing',
  TOPIC2 = 'pricing',
  TOPIC3 = 'lead generation',
  TOPIC4 = 'hiring',
  TOPIC5 = 'training',
}

// eslint-disable-next-line no-shadow
export enum RecaptchaActions {
  Login = 'login',
  Register = 'register',
}

/**
 * Where a public registration originated from. Edge always sends its source explicitly;
 * marketing forms do not, so the backend falls back to Marketing.
 */
// eslint-disable-next-line no-shadow
export enum RegistrationSource {
  EDGE = 'Edge',
  MARKETING = 'Marketing',
}

// eslint-disable-next-line no-shadow
export enum PartnerCategory {
  ALL = 'ALL',
  CUSTOMER_SUPPORT = 'CUSTOMER_SUPPORT',
  DISTRIBUTORS = 'DISTRIBUTORS',
  EQUIPMENT_TOOLS_SUPPLIES = 'EQUIPMENT_TOOLS_SUPPLIES',
  FIELD_OPERATIONS = 'FIELD_OPERATIONS',
  FINANCE = 'FINANCE',
  HIRING_TRAINING = 'HIRING_TRAINING',
  LEGAL_INSURANCE = 'LEGAL_INSURANCE',
  SALES_MARKETING = 'SALES_MARKETING',
  VEHICLES = 'VEHICLES',
}
